/**
 * Canvas helpers for react-easy-crop output.
 * Produces a square File suitable for FormData field `profilepicture`.
 * Based on the official react-easy-crop cropped-output pattern.
 */

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (err) => reject(err));
    // blob: URLs do not need CORS; http(s) gallery/CDN may
    if (!String(url).startsWith("blob:")) {
      image.setAttribute("crossOrigin", "anonymous");
    }
    image.src = url;
  });
}

function rad(deg) {
  return (deg * Math.PI) / 180;
}

function rotateSize(width, height, rotation) {
  const rotRad = rad(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * @param {string} imageSrc
 * @param {{ x: number, y: number, width: number, height: number }} pixelCrop
 * @param {{ rotation?: number, outputSize?: number, mime?: string, quality?: number, fileName?: string }} [opts]
 * @returns {Promise<File>}
 */
export async function getCroppedAvatarFile(imageSrc, pixelCrop, opts = {}) {
  const {
    rotation = 0,
    outputSize = 512,
    mime = "image/jpeg",
    quality = 0.88,
    fileName = "avatar.jpg",
  } = opts;

  if (!imageSrc || !pixelCrop?.width || !pixelCrop?.height) {
    throw new Error("Invalid crop input");
  }

  const image = await createImage(imageSrc);
  const rot = Number(rotation) || 0;
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rot);

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bBoxWidth));
  canvas.height = Math.max(1, Math.round(bBoxHeight));

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(rad(rot));
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const cropped = document.createElement("canvas");
  const cropW = Math.max(1, Math.round(pixelCrop.width));
  const cropH = Math.max(1, Math.round(pixelCrop.height));
  cropped.width = cropW;
  cropped.height = cropH;

  const cropCtx = cropped.getContext("2d");
  if (!cropCtx) throw new Error("Canvas not supported");

  cropCtx.drawImage(
    canvas,
    Math.round(pixelCrop.x),
    Math.round(pixelCrop.y),
    cropW,
    cropH,
    0,
    0,
    cropW,
    cropH
  );

  const out = document.createElement("canvas");
  out.width = outputSize;
  out.height = outputSize;
  const outCtx = out.getContext("2d");
  if (!outCtx) throw new Error("Canvas not supported");

  outCtx.imageSmoothingEnabled = true;
  outCtx.imageSmoothingQuality = "high";
  outCtx.fillStyle = "#000000";
  outCtx.fillRect(0, 0, outputSize, outputSize);
  outCtx.drawImage(cropped, 0, 0, outputSize, outputSize);

  const blob = await new Promise((resolve, reject) => {
    out.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Failed to encode image"))),
      mime,
      quality
    );
  });

  return new File([blob], fileName, { type: mime, lastModified: Date.now() });
}
